import type { NormalizedComment, NormalizedIssue, NormalizedRemedy } from "@common/types/issue";
import { resolveRemedyOoxml } from "@common/utils/liveReviewOoxml";

import {
    applyOoxmlChanges,
    getExpandedRangeForBookmarks,
    withWordContextRead,
    withWordContextWrite,
    writeReviewComment,
} from "./document";
import { OfficeDocumentBookmarks } from "./anchor/office/office-document-bookmarks";
import { OfficeWordSession } from "./anchor/office/office-word-document";
import { TableReadCache } from "./anchor/office/table-read-cache";
import type { PageParagraphRead } from "./anchor/word-document";
import { z } from "zod";

import { bulkApply, chooseConflictOptions, fetchReport, type LiveReviewBulkApplyComment, type LiveReviewBulkApplyRemedy } from "./api";
import type { BulkApplyRunSource } from "../context/BulkApplyRunContext";
import { type BookmarkedPageRead, everyRemedyCarriesTexts, type PageRangeReader } from "./redline-bulk-apply-local";
import { createRedlineAttemptRecorder } from "./redline-apply-recorder";
import { captureRedlineSnapshot, startApplyAttempt } from "./redline-snapshot";
import { safeFetch } from "./safe-fetch";
import { APPLY_FAILURE_LOCATION_NOT_FOUND } from "@common/constants/appliedActions";
import { OoxmlRange } from "@common/utils/redline/ooxml-range";
import { DIOPTRA_AUTHOR } from "@common/utils/redline/redline-engine";
import { type RedlineTextProposal, redlineTextProposalSchema, textsPayloadFrom } from "@common/utils/redline/wire/redline-payload";
import { anchorTrackers, officeWordDocument } from "./anchor/anchor-tracker-registry";
import { ProposalApplier } from "./anchor/proposal/proposal-applier";
import { DEFAULT_RECOVERY_CONFIG } from "./anchor/recovery-config";
import { ApplyFailureError, CANNOT_LOCATE_TARGET_MESSAGE, UserFacingError } from "./errors";
import { ProposalEngineAccess } from "./proposal-engine-access";
import { appliedCardsAfterWriteError, appliedCommentReportOf, appliedEditReportOf, attemptedEditOf, tellAgentsCardsApplied } from "./redline-applied-cards";
import { readLandingPageFacts } from "./redline-landing-page-facts";
import { auditCardLandings, type LandingTarget, logLandingMismatches } from "./redline-landing-reconciliation";
import { reportError } from "./sentry-reporter";
import { commentContentOfIssueComment, issueCommentBookmarksOf, recoverCommentTargetWithin } from "../components/chat/proposal-card/comment-target-recovery";

export interface ApplyRemedyArgs {
    remedy: NormalizedRemedy;
    reviewId: string | null;
    author?: string;
}

const TEXT_REMEDY_NEEDS_PROPOSAL_ENGINE_MESSAGE = "This remedy was prepared for the proposal engine, which is turned off for this account.";

/**
 * Applies a remedy the review stored as text: the add-in builds the tracked
 * changes itself from the remedy's desired paragraph texts, the way it does for
 * a text card in chat. Such a remedy exists only when the review ran with the
 * proposal engine, so the engine being off for this account is a hard stop.
 */
async function applyTextRemedy(textRedline: RedlineTextProposal, bookmarkNames: string[], author: string | undefined, remedyId: string): Promise<void> {
    const access = await ProposalEngineAccess.current();
    /**
     * WHEN the account has the proposal engine turned off
     *   CONSEQUENCE
     *     Throw a user-facing error: nothing in the legacy path can write a text remedy.
     */
    if (!access.enabled) throw new UserFacingError(TEXT_REMEDY_NEEDS_PROPOSAL_ENGINE_MESSAGE);
    /**
     * CASES for the tracked-change author
     *   - caller named one          -> use it
     *   - account has an author name -> use it
     *   - otherwise                  -> keep the author the review stored on the remedy
     */
    const redlineAuthorName = author ?? access.authorName ?? textRedline.author;
    await new ProposalApplier(anchorTrackers, DEFAULT_RECOVERY_CONFIG).applyOnBookmarks({
        bookmarkNames,
        redline: { ...textsPayloadFrom(textRedline), author: redlineAuthorName },
        cardId: remedyId,
    });
}

/**
 * Writes one remedy, then audits where it landed, as a chat card's Apply does
 * (`use-apply-redline`): a remedy that reads differently from what was written is
 * logged (`logLandingMismatches`), and the agents are told the remedy was applied
 * (`tellAgentsCardsApplied`), so their views read the page now and the next message
 * carries it.
 *
 * CASES, for what the audit judges
 *   - a remedy stored as text -> its paragraphs' texts, as a text card's
 *   - a remedy stored as markup -> the markup it resolves to, as a markup card's
 *
 * WHEN the write throws after part of the remedy landed
 *   CONSEQUENCE
 *     the agents are told it applied in part (`appliedCardsAfterWriteError`), and the
 *     caller hears the error.
 *
 * WHEN the audit throws
 *   CONSEQUENCE
 *     the error goes to Sentry and the apply still succeeds: the remedy was written.
 */
export async function applyRemedy(applyRemedyArgs: ApplyRemedyArgs): Promise<void> {
    const { remedy: remedyBeingAudited, author: requestedAuthorName } = applyRemedyArgs;
    const bookmarkNames = remedyBeingAudited.targetBookmarks ?? [];
    /** NULL card id: a review's remedy is no chat card, so it carries no number and is gathered with nothing. */
    const attemptedEdit = attemptedEditOf(null, bookmarkNames, remedyBeingAudited.textRedline);
    try {
        await writeRemedy(applyRemedyArgs);
    } catch (writeError) {
        tellAgentsCardsApplied(appliedCardsAfterWriteError(attemptedEdit, writeError));
        throw writeError;
    }
    const landingTarget: LandingTarget = remedyBeingAudited.textRedline !== null
        ? { kind: "text_card", ruleId: remedyBeingAudited.id, bookmarkNames, card: remedyBeingAudited.textRedline, insertsAfterBookmarks: false }
        : { kind: "markup", ruleId: remedyBeingAudited.id, bookmarkNames, packageOoxmls: [resolveRemedyOoxml(remedyBeingAudited, { author: requestedAuthorName ?? DIOPTRA_AUTHOR })], insertsAfterBookmarks: false };
    try {
        logLandingMismatches(await auditCardLandings([landingTarget], readLandingPageFacts), [landingTarget]);
    } catch (auditError) {
        reportError(auditError, { tags: { component: "LiveReviewApply", action: "audit_remedy_landing" } });
    }
    tellAgentsCardsApplied([appliedEditReportOf(attemptedEdit, false)]);
}

/**
 * Writes one remedy into the document, by the path the review stored it for.
 *
 * WHY apart from the audit
 *   `applyRemedy` audits the landing only once this write returns: a write that
 *   throws fails the apply, while an audit that throws is only reported, because
 *   the remedy is already in the document.
 *
 * CASES
 *   - no target bookmarks             -> throws: nothing anchors the redline
 *   - a remedy stored as text         -> the add-in builds the redline (`applyTextRemedy`)
 *   - stored as markup, proposal
 *     engine enabled                  -> the engine writes it over the bookmarks
 *   - stored as markup, engine off    -> the markup is inserted over the bookmarks' range,
 *                                        each attempt recorded
 */
async function writeRemedy({ remedy, reviewId, author }: ApplyRemedyArgs): Promise<void> {
    /**
     * WHEN no target bookmarks are present
     *   CONSEQUENCE
     *     Throw before any Word work starts: there is no anchor left for the redline.
     */
    if (!remedy.targetBookmarks?.length) {
        throw new Error("Remedy is missing targetBookmarks");
    }
    /**
     * WHEN the review stored this remedy as text
     *   CONSEQUENCE
     *     The add-in builds the redline itself; no markup path applies.
     */
    if (remedy.textRedline !== null) {
        return applyTextRemedy(remedy.textRedline, remedy.targetBookmarks, author, remedy.id);
    }
    const applyWithProposalEngine = async (authorName: string): Promise<void> => {
        const redlineOoxml = resolveRemedyOoxml(remedy, { author: authorName });
        /**
         * WHEN resolveRemedyOoxml returns nothing
         *   CONSEQUENCE
         *     Throw: this remedy has no applyable redline payload for the proposal-engine path.
         */
        if (!redlineOoxml) {
            throw new Error("Remedy has no ooxmlAfter and no trackedChanges to apply");
        }
        await new ProposalApplier(anchorTrackers, DEFAULT_RECOVERY_CONFIG).applyOnBookmarks({
            bookmarkNames: remedy.targetBookmarks ?? [],
            redline: { kind: "ooxml", redlineOoxml, author: authorName },
            cardId: remedy.id,
        });
    };
    const access = await ProposalEngineAccess.current();
    /**
     * CASES
     *   - proposal engine enabled -> use bookmark recovery path
     *   - author name             -> author ?? access.authorName ?? DIOPTRA_AUTHOR
     */
    if (access.enabled) {
        const redlineAuthorName = author ?? access.authorName ?? DIOPTRA_AUTHOR;
        return applyWithProposalEngine(redlineAuthorName);
    }
    const ooxml = resolveRemedyOoxml(remedy, { author });
    /**
     * WHEN resolveRemedyOoxml returns nothing
     *   CONSEQUENCE
     *     Throw: the legacy path also needs concrete OOXML to insert.
     */
    if (!ooxml) {
        throw new Error("Remedy has no ooxmlAfter and no trackedChanges to apply");
    }
    const bookmarks = remedy.targetBookmarks;
    const recorder = createRedlineAttemptRecorder({ initiatedFrom: "live_review", reviewId });
    let rangeResolved = true;
    let backendRepairWasAttempted = false;
    const noteWhetherBackendRepairStarted = (repairAttempt: number | null): void => {
        /**
         * WHEN onRepairStatus reports a non-null repair attempt
         *   CONSEQUENCE
         *     Mark this apply as having used backend repair so success is not recorded as clean.
         */
        if (repairAttempt !== null) backendRepairWasAttempted = true;
    };

    const applyContext = startApplyAttempt({ capture: await captureRedlineSnapshot(), reviewId });

    try {
        await withWordContextWrite(async (context) => {
            const range = await getExpandedRangeForBookmarks(context, bookmarks);
            /**
             * WHEN the bookmarks no longer resolve to a Word range
             *   CONSEQUENCE
             *     Record a range_unresolved failure and throw CANNOT_LOCATE_TARGET_MESSAGE.
             */
            if (!range) {
                rangeResolved = false;
                const unresolved = new ApplyFailureError(CANNOT_LOCATE_TARGET_MESSAGE, APPLY_FAILURE_LOCATION_NOT_FOUND);
                recorder.recordFailureAndKeepItsSnapshotId({
                    applyContext,
                    failure: { stage: "range_unresolved", bookmarks, attemptedOoxml: ooxml },
                    error: unresolved,
                });
                throw unresolved;
            }
            await applyOoxmlChanges(context, range, ooxml, false, applyContext, recorder, bookmarks, {
                origin: "pipeline",
                onRepairStatus: noteWhetherBackendRepairStarted,
            });
        });
    } catch (applyError) {
        /**
         * CASES
         *   - rangeResolved   -> hard_failure: apply failed after the target was found
         *   - !rangeResolved  -> range_unresolved: the target location could not be found
         */
        recorder.recordAttemptEndedAs(rangeResolved ? "hard_failure" : "range_unresolved");
        throw applyError;
    }

    /**
     * CASES
     *   - backendRepairWasAttempted  -> recovered_backend_repair
     *   - otherwise                  -> applied_clean
     */
    recorder.recordAttemptEndedAs(backendRepairWasAttempted ? "recovered_backend_repair" : "applied_clean");
}

/**
 * Writes one Issue comment on its paragraph, then tells the agents it was applied
 * (`tellAgentsCardsApplied`): Word raises no event for a comment, so this is how the
 * next message carries it.
 */
export async function applyComment(
    comment: NormalizedComment,
    fallbackBookmarks?: string[],
): Promise<void> {
    const bookmarks = issueCommentBookmarksOf(comment, fallbackBookmarks);
    /**
     * WHEN neither comment.bookmark nor fallbackBookmarks provides an anchor
     *   CONSEQUENCE
     *     Throw: there is no bookmark to resolve for the comment target.
     */
    if (!bookmarks.length) {
        throw new Error("Comment is missing bookmark");
    }
    await withWordContextWrite(async (context) => {
        /**
         * The comment's paragraph resolved through the chain Go to issue uses, from the
         * anchors the Issue's comment carries, before its bookmarks are read: Word drops a
         * bookmark from a paragraph retyped with Track Changes off (N28), and the card's
         * tracker lays it again on the paragraph it finds (`recoverCommentTargetWithin`).
         */
        await recoverCommentTargetWithin(context, commentContentOfIssueComment(comment, bookmarks));
        const range = await getExpandedRangeForBookmarks(context, bookmarks);
        /**
         * WHEN the comment bookmarks no longer resolve to a Word range, its recovery
         *      included
         *   CONSEQUENCE
         *     Throw CANNOT_LOCATE_TARGET_MESSAGE with APPLY_FAILURE_LOCATION_NOT_FOUND.
         */
        if (!range) {
            throw new ApplyFailureError(CANNOT_LOCATE_TARGET_MESSAGE, APPLY_FAILURE_LOCATION_NOT_FOUND);
        }
        /**
         * WHEN the review said where the comment sits (first word, quoted words, the
         * paragraph inserted after this one)
         *   CONSEQUENCE
         *     the comment is written on that narrower range; with no placement (a
         *     production review) it covers the bookmarked range as before.
         */
        await writeReviewComment(context, {
            bookmarkedRange: range,
            placement: comment.placement,
            anchorText: comment.anchorText,
        }, comment.text);
    });
    /** NULL card id: a review's comment is no chat card. */
    tellAgentsCardsApplied([appliedCommentReportOf({ cardId: null, paragraphBookmarkNames: bookmarks, text: comment.text, quotedText: comment.anchorText ?? null })]);
}

export interface ReviewBulkEntries {
    remedies: LiveReviewBulkApplyRemedy[];
    comments: LiveReviewBulkApplyComment[];
}

/** Why an issue's remedies were proposed, for the edit model when one contests another card's words: the rule's name, its text and the reasoning the review gave. */
const issueIntent = (issue: NormalizedIssue): string =>
    [issue.name, issue.description, issue.reasoning].filter((part): part is string => part !== undefined && part.length > 0).join("\n\n");

/**
 * The markup a bulk entry carries for one remedy: what the server pipeline writes
 * for it when the run goes that way. The local fold ignores it and builds every
 * text card over the page.
 *
 * CASES
 *   - its live preview is up            -> that package, the redline over the page as it was last read
 *   - a production remedy with markup   -> its stored markup
 *   - a text card with no preview yet   -> none; only the fold can build it
 */
function markupToWriteFor(remedy: NormalizedRemedy): string[] {
    if (remedy.textRedline !== null) return [];
    return remedy.ooxmlAfter ?? [];
}

/**
 * A review's selected issues as the entries Apply All collects from chat cards,
 * so the review page and the live-review drawer run the same local Apply All.
 *
 * A comment with no bookmark of its own sits on the issue's first detection
 * bookmark; one with neither has nowhere to go and is left out.
 */
export function bulkEntriesOfIssues(issues: NormalizedIssue[], ruleIds: string[]): ReviewBulkEntries {
    const selected = issues.filter((issue) => issue.ruleUuid !== undefined && ruleIds.includes(issue.ruleUuid));
    const remedies = selected.flatMap((issue) => (issue.remedies ?? []).map((remedy): LiveReviewBulkApplyRemedy => ({
        rule_uuid: issue.ruleUuid ?? "",
        rule_name: issue.name,
        target_bookmark_names: remedy.targetBookmarks,
        target_ooxml_after: markupToWriteFor(remedy),
        target_texts: remedy.textRedline,
        intent: issueIntent(issue),
    })));
    const comments = selected.flatMap((issue) => (issue.comments ?? []).flatMap((comment): LiveReviewBulkApplyComment[] => {
        const bookmark = comment.bookmark ?? issue.detectionBookmarks?.[0];
        if (bookmark === undefined) return [];
        return [{ rule_uuid: issue.ruleUuid ?? "", rule_name: issue.name, bookmark, text: comment.text, placement: comment.placement, anchor_text: comment.anchorText, issue_comment_id: comment.id }];
    }));
    return { remedies, comments };
}

/**
 * The page the local fold lays its cards on: the bookmarked paragraphs read whole,
 * the same read the applier builds a single card over. Apply All cuts the same page
 * out of its one whole-document read (`readPagesFromOneDocumentRead`) and asks Word
 * here only for a page that read cannot cut.
 *
 * WHY
 *   Word's OOXML of a bookmark's own range stops before the paragraph mark, and
 *   the mark is what carries `w:pPr` (the style, the numbering). A package built
 *   over that read writes the paragraph with no properties, and the writer's
 *   whole-paragraph replace then turns a numbered item into Normal.
 *
 * EXAMPLE
 *   bookmark range read   -> <w:p><w:r>…</w:r></w:p>                          numbering lost
 *   whole paragraph read  -> <w:p><w:pPr><w:pStyle w:val="ListNumber"/></w:pPr>…   numbering kept
 *
 * WHY the cell flag is read in the same run
 *   Word Online's markup of a cell paragraph carries no cell around it, so the
 *   fold learns the group writes into a table from Office.js, against the same
 *   paragraphs the markup was read from.
 *
 * WHY the rows of every bookmark the cards list are read too
 *   For the same reason the markup cannot say which row a cell is in, and Apply All
 *   reads a cell as struck only where it places the cell in a row a card deletes
 *   (`collisionParagraphsOf`). A card that strikes a row lists every cell of it, the
 *   ones the agent did not name included, so each is placed by Office.js
 *   (`OfficeDocumentBookmarks.cellRowKeysOf`).
 *
 * WHY a page with a table cell is read paragraph by paragraph
 *   Word exports a range that crosses table rows as its first row alone (live probe,
 *   Word for Mac 16.114, 2026-10-16: a span over rows 1-3 of a question table read as
 *   row 1's three cells). The fold composes a table card from the paragraphs on its
 *   page, so a card that rewrote a question in each of seven rows wrote only row 1's
 *   and reported Applied (R35, production, 2026-10-16). Each listed paragraph read on
 *   its own (`OfficeWordSession.readBookmarkedParagraphs`) always holds that paragraph.
 *
 * WHY no recovery here
 *   The run found every card's bookmarks again before the fold, as applying one
 *   card finds them (`AnchorTrackerRegistry.placementsForApply`, called by
 *   `BulkApplyRunProvider`), and took the cards recovery placed beside a
 *   paragraph out of the fold; the fold reads only cards laid on their paragraphs.
 *
 * WHY the rows are read before the span
 *   A page with a cell never uses the span read, and each Word round trip is time the
 *   user waits through: read first, the rows say whether the span is needed at all.
 *
 * CASES
 *   - no listed paragraph sits in a cell -> the span read, whole paragraphs with their marks
 *   - one or more do                     -> every listed paragraph read on its own
 *                                           (`tablePageReadOneByOne`), and the span is never read
 *
 * NULL
 *   none of the bookmarks is in the document any more.
 */
export const readPageParagraphs = (bookmarkNames: string[], cards: LiveReviewBulkApplyRemedy[] = []): Promise<BookmarkedPageRead | null> =>
    withWordContextRead(async (context) => {
        const documentBookmarks = new OfficeDocumentBookmarks(context, DEFAULT_RECOVERY_CONFIG);
        const listedBookmarkNames = listedBookmarkNamesOf(bookmarkNames, cards);
        const cellRowKeyByBookmarkName = await documentBookmarks.cellRowKeysOf(listedBookmarkNames);
        /**
         * WHEN Word places a listed paragraph in a table cell
         *   CONSEQUENCE
         *     the page is read paragraph by paragraph and the span is never read: Word
         *     would export a span crossing rows as its first row alone.
         */
        if (cellRowKeyByBookmarkName.size > 0) return tablePageReadOneByOne(context, listedBookmarkNames, cellRowKeyByBookmarkName);
        const read = await documentBookmarks.readRange(bookmarkNames);
        /**
         * WHEN none of the bookmarks resolves
         *   CONSEQUENCE
         *     null: the fold reports the group lost.
         */
        if (read === null) return null;
        return { pageOoxml: read.rangeOoxml, wordsExportAsGiven: read.wordsExportAsGiven, bookmarkedParagraphSitsInTableCell: read.bookmarkedParagraphSitsInTableCell, cellRowKeyByBookmarkName };
    });

/**
 * Every bookmark a group's page holds when it writes into a table: the group's own, then
 * each bookmark its cards list, once. A card that strikes a row lists every cell of it,
 * the ones the agent did not name included, so each of them is read and placed in its row.
 *
 * EXAMPLE
 *   group [_row1_ask], its card listing [_row1_cite, _row1_ask, a new line with no bookmark]
 *   -> [_row1_ask, _row1_cite]
 */
function listedBookmarkNamesOf(bookmarkNames: string[], cards: LiveReviewBulkApplyRemedy[]): string[] {
    /** A card paragraph with no bookmark names no cell to place. */
    const bookmarksTheCardsList = cards.flatMap((card) => (card.target_texts?.paragraphs ?? []).flatMap((cardParagraph) => (cardParagraph.bookmark === null ? [] : [cardParagraph.bookmark])));
    return Array.from(new Set([...bookmarkNames, ...bookmarksTheCardsList]));
}

/**
 * The page of a table group: every listed paragraph read on its own, laid in the
 * document's order (`pageOfParagraphsReadOneByOne`), with the listed bookmarks Word no
 * longer finds, so the fold can tell a cell that is gone from one Word did not return.
 *
 * EXAMPLE
 *   listed [_row1_ask, _row2_ask, _row3_ask], _row3_ask's row deleted by the user
 *   -> page holds _row1_ask and _row2_ask; bookmarkNamesNotFoundInDocument {_row3_ask}
 */
async function tablePageReadOneByOne(context: Word.RequestContext, listedBookmarkNames: string[], cellRowKeyByBookmarkName: Map<string, string>): Promise<BookmarkedPageRead> {
    const paragraphsReadOneByOne = await new OfficeWordSession(context, DEFAULT_RECOVERY_CONFIG, new TableReadCache(), officeWordDocument.events).readBookmarkedParagraphs(listedBookmarkNames);
    const bookmarkNamesRead = new Set(paragraphsReadOneByOne.map((paragraphRead) => paragraphRead.bookmarkName));
    return {
        pageOoxml: pageOfParagraphsReadOneByOne(paragraphsReadOneByOne),
        bookmarkedParagraphSitsInTableCell: true,
        cellRowKeyByBookmarkName,
        bookmarkNamesNotFoundInDocument: new Set(listedBookmarkNames.filter((bookmarkName) => !bookmarkNamesRead.has(bookmarkName))),
    };
}

/**
 * The page of a table card: each listed paragraph from its own read, in the
 * document's order, one package.
 *
 * CASES, per read
 *   - a paragraph of the read answers to the bookmark, or takes it back
 *     (`OoxmlRange.paragraphReadOfBookmark`)                        -> that paragraph goes on the page
 *   - none does (a row read without the bookmark)                    -> nothing: the card's other cells are written,
 *                                                                       and the run reports it partly applied, naming
 *                                                                       the cell (`partlyAppliedCardsOf`)
 *   - a second bookmark resolves into a paragraph already on the page -> nothing more: the paragraph goes once
 *
 * EXAMPLE
 *   reads   _row2_ask (body index 9), _row1_ask (body index 5), _row1_ask_dup (index 5)
 *   -> page <w:p _row1_ask/><w:p _row2_ask/>
 */
function pageOfParagraphsReadOneByOne(paragraphsRead: PageParagraphRead[]): string {
    /**
     * EXAMPLE
     *   body indexes 9 and 5   -> 9 - 5 = 4 > 0   -> index 5 goes first
     */
    const inDocumentOrder = [...paragraphsRead].sort((first, second) => first.paragraphIndex - second.paragraphIndex);
    const paragraphIdsOnPage = new Set<string>();
    const paragraphXmls = inDocumentOrder.flatMap((paragraphRead) => {
        /** WHEN another bookmark already put this paragraph on the page CONSEQUENCE it goes once. */
        if (paragraphIdsOnPage.has(paragraphRead.uniqueLocalId)) return [];
        const paragraphOfBookmark = OoxmlRange.paragraphReadOfBookmark(paragraphRead.paragraphOoxml, paragraphRead.bookmarkName);
        /** WHEN the read cannot say which paragraph is the bookmark's CONSEQUENCE none goes on the page. */
        if (paragraphOfBookmark === null) return [];
        paragraphIdsOnPage.add(paragraphRead.uniqueLocalId);
        /** The package holds the one paragraph, at index 0. */
        return [new OoxmlRange(paragraphOfBookmark).paragraphXml(0)];
    });
    return OoxmlRange.packageAround(paragraphXmls.join(""));
}

/**
 * One Apply All run's page reader: every page cut from the whole-document read the run
 * hands it (`OoxmlRange.bookmarkedPagesOf`, `LocalBulkApplyInput.documentPagesOfTheRun`),
 * the same page `readPageParagraphs` reads from Word, so a pass of the run reads Word once
 * instead of once per group of cards.
 *
 * WHICH read
 *   The one the run found its cards on (`AnchorTrackerRegistry.placementsForApply`), or one
 *   it made again after a bookmark was laid or a write landed. The fold asks for every page
 *   before it writes anything, so the read is of the document the fold lays its cards on.
 *
 * CASES, for one group's bookmarks
 *   - none of them is in the read                      -> null, as `readPageParagraphs` returns
 *   - a listed paragraph sits in a table cell, and
 *     the read holds every listed bookmark             -> the page cut paragraph by paragraph, every
 *                                                         listed cell placed in its row by the read
 *                                                         (`BookmarkedPages.tablePageOf`)
 *   - a listed bookmark is missing from the read, or
 *     the paragraphs are a block content control's     -> `readPageParagraphs`: Word finds what its
 *                                                         export left out, names a deleted cell, and
 *                                                         shapes a content control's export itself
 *   - otherwise                                        -> the page cut from the read
 *
 * WHY the pages read from Word go one at a time
 *   The fold asks for every group's page at once. Word for Windows refuses a request that
 *   arrives while it is still answering another ("Wait until the previous call completes."),
 *   and Word answers reads one after another anyway, so queueing them costs no time.
 *
 * FALLBACK
 *   The run holds no whole-document read (Word threw while reading): every page is read
 *   from Word.
 */
export function readPagesFromOneDocumentRead(): PageRangeReader {
    /** The last page read from Word this run; the next one starts once it has settled, read or failed. */
    let previousWordPageRead: Promise<unknown> = Promise.resolve();
    /**
     * One page read from Word, after every page read from Word before it.
     *
     * EXAMPLE
     *   the fold asks for the pages of _fee and _term at once, both table cells
     *   -> _fee is read; _term is read once _fee's read has settled
     */
    const readPageFromWordInTurn = (bookmarkNames: string[], cards: LiveReviewBulkApplyRemedy[]): Promise<BookmarkedPageRead | null> => {
        const pageRead = previousWordPageRead.then(() => readPageParagraphs(bookmarkNames, cards));
        previousWordPageRead = pageRead.catch(() => undefined);
        return pageRead;
    };
    return async (bookmarkNames, cards, documentPagesOfTheRun) => {
        /**
         * WHEN the run holds no whole-document read
         *   CONSEQUENCE
         *     this page is read from Word, through the FALLBACK above.
         */
        if (documentPagesOfTheRun === null) return readPageFromWordInTurn(bookmarkNames, cards);
        const applyPage = documentPagesOfTheRun.applyPageOf(bookmarkNames, listedBookmarkNamesOf(bookmarkNames, cards));
        /** WHEN none of the bookmarks is in the read CONSEQUENCE null, as `readPageParagraphs` returns. */
        if (applyPage === null) return null;
        /**
         * WHEN a listed paragraph sits in a table cell and the read holds every listed bookmark
         *   CONSEQUENCE
         *     the page Word's read of each cell on its own gives, cut from the same read, every
         *     listed cell placed in its row by the read (`tablePageOf`), without a Word read.
         */
        if (applyPage.kind === "tablePage") return { pageOoxml: applyPage.packageOoxml, bookmarkedParagraphSitsInTableCell: true, cellRowKeyByBookmarkName: applyPage.cellRowKeyByBookmarkName, bookmarkNamesNotFoundInDocument: new Set<string>() };
        /**
         * WHEN the page was cut from the whole-document read
         *   CONSEQUENCE
         *     none of its paragraphs sits in a cell: `bookmarkedPagesOf` sends a page
         *     with any paragraph outside the body (a cell, a block content control) to
         *     `readFromWord`, cut above as a table page or read from Word below with
         *     Office.js's cell flag.
         */
        if (applyPage.kind === "page") return { pageOoxml: applyPage.packageOoxml, bookmarkedParagraphSitsInTableCell: false };
        return readPageFromWordInTurn(bookmarkNames, cards);
    };
}

/**
 * The issues with their main remedies only: every alternative remedy a playbook
 * rule offers (`isPrimary` false) is left out.
 *
 * WHY
 *   The issue row and the issue drawer show only the main remedy, so an
 *   alternative is a redline the user never saw. It shares its issue's rule id,
 *   so the add-in's fold does not zone it against the main remedy; it reaches the
 *   core's contested fold as a second version laid over the main one.
 *
 * EXAMPLE
 *   issue "Payment terms"   remedies [main "forty-five days", alternative "sixty days"]
 *   -> issue "Payment terms"   remedies [main "forty-five days"]
 */
function issuesWithMainRemediesOnly(issues: NormalizedIssue[]): NormalizedIssue[] {
    return issues.map((issue) => ({ ...issue, remedies: issue.remedies?.filter((remedy) => remedy.isPrimary) }));
}

/**
 * Where a review's Apply All takes its run from, for `BulkApplyRunProvider.startRun`.
 *
 * WHEN every remedy is a text card (the review ran on the proposal engine)
 *   CONSEQUENCE
 *     the run is folded in the add-in from the entries above, the same local Apply
 *     All the chat panel runs, and the provider traces the fold and writes the
 *     entries; a production remedy anywhere keeps the playbook pipeline for the
 *     whole run, posted here so the status file exists before the run starts.
 *
 * WHEN the run is folded in the add-in
 *   CONSEQUENCE
 *     it carries each issue's main remedies only (`issuesWithMainRemediesOnly`);
 *     the playbook pipeline run is unchanged.
 */
export async function reviewBulkApplySource(authHeader: string, reviewId: string, issues: NormalizedIssue[], ruleIds: string[]): Promise<BulkApplyRunSource> {
    const { remedies, comments } = bulkEntriesOfIssues(issues, ruleIds);
    const runIsLocal = everyRemedyCarriesTexts(remedies);
    if (!runIsLocal) return { kind: "pipeline", statusFileUrl: await bulkApply(authHeader, reviewId, ruleIds), remedies };
    return {
        kind: "local",
        input: {
            remedies: bulkEntriesOfIssues(issuesWithMainRemediesOnly(issues), ruleIds).remedies,
            comments,
            readPageRange: readPagesFromOneDocumentRead(),
            chooseConflictOptions: (paragraphs) => chooseConflictOptions(authHeader, paragraphs),
            coveredSpansOver: (bookmarkNames, pageOoxml) => anchorTrackers.coveredSpansOver(bookmarkNames, pageOoxml),
        },
    };
}

const StepPackageSchema = z.object({ ref: z.string(), url: z.string() });

const StepPackagesSchema = z.object({
    before: StepPackageSchema,
    after: StepPackageSchema,
    revisions: z.record(z.string(), z.number()).optional(),
});

const ReviewReportPlanSchema = z.array(
    z.object({
        steps: z
            .array(
                z.object({
                    action_report: z
                        .object({
                            target_ooxml_after: z.array(z.string()).optional(),
                            target_packages: StepPackagesSchema.optional(),
                            /**
                             * Proposal engine only: absent on a report a production review wrote.
                             *
                             * WHEN the stored card fails its schema
                             *   CONSEQUENCE
                             *     it reads as absent, so that one step carries no card and every other
                             *     step and rule of the report still reads (one card never blocks a run).
                             */
                            target_texts: redlineTextProposalSchema.optional().catch(undefined),
                        })
                        .optional(),
                }),
            )
            .optional(),
    }),
);

const ReviewReportEntrySchema = z.object({
    rule: z.object({ uuid: z.string().optional() }).optional(),
    action_plan: ReviewReportPlanSchema.optional(),
    alternative_action_plans: z.array(ReviewReportPlanSchema).optional(),
});

const ReviewReportResponseSchema = z.object({
    report: z.array(z.unknown()).transform(reviewReportEntriesThatParse),
});

type ReviewReportEntry = z.infer<typeof ReviewReportEntrySchema>;

/**
 * The review report's rule entries, each read on its own.
 *
 * WHY one rule never blocks a run
 *   Apply All reads one remedy's markup out of the whole report. Read as one
 *   array, a single entry the schema refuses made the whole report "malformed",
 *   and no rule's redline could be loaded.
 *
 * CASES
 *   - the entry parses -> kept
 *   - it fails         -> left out: a remedy of that rule finds no entry, the same
 *                         as a rule the report does not hold (`remedyOoxmlFromReport`)
 */
function reviewReportEntriesThatParse(rawReportEntries: unknown[]): ReviewReportEntry[] {
    return rawReportEntries.flatMap((rawReportEntry) => {
        const reportEntry = ReviewReportEntrySchema.safeParse(rawReportEntry);
        /**
         * CASES
         *   - parsed  -> [the entry]
         *   - refused -> []: the entry is left out
         */
        return reportEntry.success ? [reportEntry.data] : [];
    });
}

const reportEntriesCache = new Map<string, Promise<ReviewReportEntry[]>>();

async function fetchReviewReportEntries(
    authHeader: string,
    reviewId: string,
): Promise<ReviewReportEntry[]> {
    const { reportFileUrl } = await fetchReport(authHeader, reviewId);
    /**
     * WHEN fetchReport has no reportFileUrl yet
     *   CONSEQUENCE
     *     Treat the review report as not published yet and fail fast.
     */
    if (!reportFileUrl) throw new Error("The review report is not available yet.");
    const response = await safeFetch(reportFileUrl);
    const parsed = ReviewReportResponseSchema.safeParse(await response.json());
    /**
     * WHEN the fetched JSON does not match ReviewReportResponseSchema
     *   CONSEQUENCE
     *     Throw: report hydration depends on the expected report shape being valid.
     */
    if (!parsed.success) {
        throw new Error("The review report is malformed and could not be read.");
    }
    return parsed.data.report;
}

function loadReviewReportEntries(
    authHeader: string,
    reviewId: string,
): Promise<ReviewReportEntry[]> {
    const cached = reportEntriesCache.get(reviewId);
    /**
     * WHEN this reviewId is already in reportEntriesCache
     *   CONSEQUENCE
     *     Reuse the in-flight or completed fetch instead of starting another download.
     */
    if (cached) return cached;
    const pending = fetchReviewReportEntries(authHeader, reviewId).catch((error) => {
        reportEntriesCache.delete(reviewId);
        throw error;
    });
    reportEntriesCache.set(reviewId, pending);
    return pending;
}

async function remedyOoxmlFromReport(
    entries: ReviewReportEntry[],
    ruleUuid: string | undefined,
    remedyId: string,
): Promise<string[]> {
    /**
     * WHEN ruleUuid is missing
     *   CONSEQUENCE
     *     Return []: the report is keyed by rule UUID before any plan, entry, or step lookup.
     */
    if (!ruleUuid) return [];
    const entry = entries.find((e) => e.rule?.uuid === ruleUuid);
    /**
     * WHEN no report entry matches ruleUuid
     *   CONSEQUENCE
     *     Return []: there is no action-plan tree to search for this remedy.
     */
    if (!entry) return [];
    const parts = remedyId.split(":");
    const planIndexPartText = parts[parts.length - 3];
    /**
     * EXAMPLE
     *   parts         = ["rule", "7", "2", "5"]
     *   parts.length  = 4
     *   parts[1]      -> "7"
     *   planIndex     = 7
     *
     * CASES
     *   - planIndex === 0  -> use entry.action_plan
     *   - planIndex > 0    -> alternative plan number from remedyId
     */
    const planIndex = Number(planIndexPartText);
    const entryIndexPartText = parts[parts.length - 2];
    /**
     * EXAMPLE
     *   parts[2]    -> "2"
     *   entryIndex  = 2
     *   plan[2]     -> third entry in the chosen plan
     */
    const entryIndex = Number(entryIndexPartText);
    const stepIndexPartText = parts[parts.length - 1];
    /**
     * EXAMPLE
     *   parts[3]     -> "5"
     *   stepIndex    = 5
     *   steps[5]     -> sixth step in the chosen plan entry
     */
    const stepIndex = Number(stepIndexPartText);
    const remedyCoordinates = [planIndex, entryIndex, stepIndex];
    const everyRemedyCoordinateIsInteger = remedyCoordinates.every(Number.isInteger);
    /**
     * WHEN any trailing remedyId coordinate is missing or non-integer
     *   CONSEQUENCE
     *     Return [] instead of indexing the report with invalid positions.
     */
    if (!everyRemedyCoordinateIsInteger) return [];
    /**
     * EXAMPLE
     *   planIndex  = 1  -> alternative_action_plans[0]
     *   planIndex  = 3  -> alternative_action_plans[2]
     *
     * WHY
     *   remedyId stores alternative plans as 1-based, but the array is 0-based.
     */
    const plan =
        planIndex === 0
            ? entry.action_plan
            : entry.alternative_action_plans?.[planIndex - 1];
    const report = plan?.[entryIndex]?.steps?.[stepIndex]?.action_report;
    /**
     * WHEN action_report already carries target_ooxml_after
     *   CONSEQUENCE
     *     Return it directly and skip package download.
     */
    if (report?.target_ooxml_after?.length) return report.target_ooxml_after;
    const packaged = report?.target_packages?.after;
    /**
     * WHEN there is no inline OOXML and no target_packages.after reference
     *   CONSEQUENCE
     *     Return []: this step exposes no retrievable redline payload.
     */
    if (!packaged) return [];
    const response = await safeFetch(packaged.url);
    return [await response.text()];
}

export async function hydrateRemedyOoxml(
    remedy: NormalizedRemedy,
    ruleUuid: string | undefined,
    ctx: { authHeader: string | null; reviewId: string | null },
): Promise<NormalizedRemedy> {
    /**
     * WHEN the remedy already has ooxmlAfter or trackedChanges, or is stored as text
     *   CONSEQUENCE
     *     Return it unchanged and skip report hydration: a text remedy is applied
     *     from its texts, so there is no markup to fetch for it.
     */
    if ((remedy.ooxmlAfter?.length ?? 0) > 0 || (remedy.trackedChanges?.length ?? 0) > 0 || remedy.textRedline !== null) {
        return remedy;
    }
    const reviewAuthHeaderIsMissing = !ctx.authHeader;
    const reviewIdIsMissing = !ctx.reviewId;
    const reviewHydrationContextIsMissing = reviewAuthHeaderIsMissing || reviewIdIsMissing;
    /**
     * WHEN hydration is needed but authHeader or reviewId is missing
     *   CONSEQUENCE
     *     Throw the review-finalizing error instead of attempting report fetches.
     */
    if (reviewHydrationContextIsMissing) {
        throw new Error(
            "We could not load this redline — the review is still finalizing. Try again in a moment.",
        );
    }
    let entries = await loadReviewReportEntries(ctx.authHeader, ctx.reviewId);
    let ooxmlAfter = await remedyOoxmlFromReport(entries, ruleUuid, remedy.id);
    /**
     * WHEN the first report lookup returns no OOXML
     *   CONSEQUENCE
     *     Drop the cached report, reload it once, and try the same remedy lookup again.
     */
    if (ooxmlAfter.length === 0) {
        reportEntriesCache.delete(ctx.reviewId);
        entries = await loadReviewReportEntries(ctx.authHeader, ctx.reviewId);
        ooxmlAfter = await remedyOoxmlFromReport(entries, ruleUuid, remedy.id);
    }
    /**
     * WHEN the retry still returns no OOXML
     *   CONSEQUENCE
     *     Throw: the review report has no recoverable redline payload for this remedy.
     */
    if (ooxmlAfter.length === 0) {
        throw new Error("This redline could not be loaded from the review report.");
    }
    return { ...remedy, ooxmlAfter };
}
